import { Component, OnInit, OnDestroy } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, zip } from "rxjs";
import { filter, first, takeUntil, switchMap, map } from "rxjs/operators";
import * as moment from "moment";
import { Language } from "@app/core/models";
import {
  PropertiesService,
  LanguageService,
  AnalyticsService,
  MobileAppService,
} from "@app/core/services";
import { User } from "@app/core/domain";
import { UserFacade, AppFacade, AuthService } from "@app/core/app-services";
import { RouterState } from "../../state/router";
import { MobileAppModalProvider } from "../mobile-app-modal";

@Component({
  selector: "ingo-main-app",
  template: ` <ingo-app-shell
    [dataReady]="dataReady"
    [loading]="loading"
    [hasError]="hasError"
    [layout]="layout$ | async"
    [accessBlocked]="accessBlocked$ | async"
    [user]="user$ | async"
    [currentLanguage]="language$ | async"
    [currentRoute]="currentRoute$ | async"
    (onSelectLanguage)="selectLanguage($event)"
    (onLogout)="logOut()"
  >
  </ingo-app-shell>`,
})
export class AppMainComponent implements OnInit, OnDestroy {
  user$: Observable<User>;
  language$: Observable<Language>;
  currentRoute$: Observable<Array<string>>;
  layout$: Observable<string>;
  accessBlocked$: Observable<boolean>;
  dataReady: boolean = false;
  loading: boolean = false;
  hasError: boolean = false;

  private unsubscribeTrigger: Subject<void> = new Subject();

  constructor(
    private properties: PropertiesService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private appFacade: AppFacade,
    private userFacade: UserFacade,
    private auth: AuthService,
    private router: Router,
    private routerState: RouterState,
    private mobileAppModal: MobileAppModalProvider,
    private analytics: AnalyticsService,
    private mobileAppService: MobileAppService
  ) {}

  ngOnInit() {
    this.user$ = this.userFacade.getCurrentUser();
    this.language$ = this.appFacade.getLanguage();
    this.currentRoute$ = this.routerState.getRouteUrlSegments();
    this.layout$ = this.routerState.getRouteLayout();
    this.accessBlocked$ = this.userFacade.ageOK().pipe(map((x) => !x));

    this.setPageViewTracking();

    let authCode: string | null = null;

    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      authCode = urlParams.get("authorization_code");
    }

    if (authCode) {
      sessionStorage.setItem("WAS_AUTH_CODE", authCode);

      this.router.navigate(["/auth-callback"]);

      this.user$
        .pipe(
          takeUntil(this.unsubscribeTrigger),
          filter((user) => !!user)
        )
        .subscribe((user) => {
          this.setLanguage(
            this.languageService.getSelectedLanguage() ||
              this.languageService.getLanguageByCode(user.lang) ||
              this.languageService.getLanguage("SWEDISH")
          );
          this.dataReady = true;
        });

      this.language$
        .pipe(
          takeUntil(this.unsubscribeTrigger),
          filter((lang) => !!lang)
        )
        .subscribe((language) => this.handleLanguageChange(language));

      return;
    }

    this.properties
      .fetch()
      .pipe(
        first(),
        switchMap(() => this.userFacade.fetchCurrentUser()),
        switchMap(() =>
          zip(
            this.userFacade.hasDateOfBirth(),
            this.userFacade.ageVerifiedNegative(),
            this.userFacade.hasMarkedMarketingConsent()
          )
        )
      )
      .subscribe(
        (res) => {
          const [hasDOB, verifiedNegative, consentMarked] = res;
          this.appFacade.setAuthenticated(true);

          if (
            !this.appFacade.shouldRedirectUser(
              hasDOB,
              verifiedNegative,
              consentMarked
            )
          ) {
            this.auth.goToOriginalRouteIfExists();
            this.handleRouteChange();
            this.showMobileAppModal();
          }

          this.dataReady = true;
        },
        (err: HttpErrorResponse) => {
          this.setLanguage(
            this.languageService.getSelectedLanguage() ||
              this.languageService.getLanguage("SWEDISH")
          );
          this.hasError = true;
          if (err.status !== 401) {
            this.appFacade.setError();
          }
        }
      );

    this.user$
      .pipe(
        takeUntil(this.unsubscribeTrigger),
        filter((user) => !!user)
      )
      .subscribe((user) =>
        this.setLanguage(
          this.languageService.getSelectedLanguage() ||
            this.languageService.getLanguageByCode(user.lang) ||
            this.languageService.getLanguage("SWEDISH")
        )
      );

    this.language$
      .pipe(
        takeUntil(this.unsubscribeTrigger),
        filter((lang) => !!lang)
      )
      .subscribe((language) => this.handleLanguageChange(language));
  }

  ngOnDestroy(): void {
    this.unsubscribeTrigger.next();
    this.unsubscribeTrigger.complete();
  }

  selectLanguage(language: Language): void {
    this.languageService.saveSelectedLanguage(language);
    this.setLanguage(language);
  }

  logOut(): void {
    this.analytics.eventTrack("log_out", { event_category: "user" });
    // delay logout to allow analytics to fire
    setTimeout(() => this.userFacade.logout(), 100);
  }

  private showMobileAppModal(): void {
    if (this.mobileAppService.isAndroid()) {
      setTimeout(() => this.mobileAppModal.open(), 300);
    }
  }

  private handleRouteChange(): void {
    this.routerState
      .getState()
      .pipe(
        takeUntil(this.unsubscribeTrigger),
        filter((route) => route !== null)
      )
      .subscribe(() => {
        this.appFacade.resetError();
        this.scrollToTop();
      });
  }

  private handleLanguageChange(language: Language): void {
    if (
      this.translate.getLangs() &&
      this.translate.getLangs().indexOf(language.code) > -1
    ) {
      this.loading = true;
      setTimeout(() => {
        this.translate.use(language.code);
        moment.locale(language.getShortCode());
        this.loading = false;
      });
    } else {
      this.loading = true;
      this.translate
        .reloadLang(language.code)
        .pipe(first())
        .subscribe(() => {
          this.translate.use(language.code);
          moment.locale(language.getShortCode());
          this.loading = false;
        });
    }
  }

  private setLanguage(language: Language): void {
    this.appFacade.setLanguage(language);
  }

  private scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  private setPageViewTracking() {
    this.routerState
      .getRouteUrlSegments()
      .pipe(takeUntil(this.unsubscribeTrigger))
      .subscribe((urlSegments) =>
        this.analytics.pageTrack("/" + urlSegments.join("/"))
      );
  }
}
